/**
 * ResourcesSection — Spec 154 Phase 3
 *
 * Static crisis resources and kink-aware directories for the Safety Hub.
 * This is informational only — Helm does not provide crisis counseling.
 *
 * Clearly separated from Helm features. All links open in new tab.
 */

import React from 'react';

// ============================================================================
// Data
// ============================================================================

interface CrisisLine {
  name: string;
  contact: string;
  description: string;
  href: string;
}

interface Directory {
  name: string;
  description: string;
  href: string;
}

interface EducationalLink {
  name: string;
  description: string;
  href: string;
}

export const CRISIS_LINES: CrisisLine[] = [
  {
    name: '988 Suicide & Crisis Lifeline',
    contact: 'Call or text 988',
    description: '24/7 free and confidential support for people in distress.',
    href: 'https://988lifeline.org',
  },
  {
    name: 'Crisis Text Line',
    contact: 'Text HOME to 741741',
    description: 'Free crisis counseling via text message, 24/7.',
    href: 'https://www.crisistextline.org',
  },
  {
    name: 'The Trevor Project',
    contact: '1-866-488-7386',
    description: 'Crisis support for LGBTQ+ young people. TrevorLifeline available 24/7.',
    href: 'https://www.thetrevorproject.org',
  },
];

const DIRECTORIES: Directory[] = [
  {
    name: 'Kink Aware Professionals (KAP)',
    description: 'National Coalition for Sexual Freedom directory of kink-aware therapists and healthcare providers.',
    href: 'https://ncsfreedom.org/key-programs/kap-directory/',
  },
  {
    name: 'Psychology Today Therapist Finder',
    description: 'Search for therapists who specialize in sexuality, relationships, and alternative lifestyles.',
    href: 'https://www.psychologytoday.com/us/therapists',
  },
];

const EDUCATIONAL_LINKS: EducationalLink[] = [
  {
    name: 'National Coalition for Sexual Freedom',
    description: 'Advocacy and education for consensual adult relationships and sexual expression.',
    href: 'https://ncsfreedom.org',
  },
  {
    name: 'The Consent Academy',
    description: 'Educational resources on consent, communication, and boundary-setting.',
    href: 'https://theconsentacademy.com',
  },
];

// ============================================================================
// Component
// ============================================================================

interface ResourcesSectionProps {
  /** If true, renders without a section heading (for embedding in collapsible) */
  headless?: boolean;
  /** DOM id for the root. Only the /safeword page instance sets one (see CrisisResources), so ids never duplicate. */
  id?: string;
}

const ExternalLink: React.FC<{ href: string; label: string; children: React.ReactNode }> = ({
  href,
  label,
  children,
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    className="block p-3 rounded-lg border border-[var(--theme-border-light)] bg-[var(--theme-bg-primary)] hover:bg-[var(--theme-bg-hover)] transition-colors duration-150 no-underline min-h-[44px]"
  >
    {children}
  </a>
);

const ResourcesSection: React.FC<ResourcesSectionProps> = ({ headless = false, id }) => {
  return (
    <div id={id} className="flex flex-col gap-4">
      {!headless && (
        <h3 className="text-[0.8rem] font-semibold text-[var(--theme-text-primary)] uppercase tracking-wider m-0">
          Safety Resources
        </h3>
      )}

      {/* Crisis Lines */}
      <div className="flex flex-col gap-2">
        <p className="text-xs font-semibold text-[var(--theme-text-secondary)] uppercase tracking-wide m-0">
          Crisis Support
        </p>
        <div className="flex flex-col gap-2">
          {CRISIS_LINES.map((line) => (
            <ExternalLink key={line.name} href={line.href} label={`Visit ${line.name}`}>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold text-[var(--theme-text-primary)]">
                  {line.name}
                </span>
                <span className="text-sm font-medium text-[var(--theme-accent,var(--theme-primary))]">
                  {line.contact}
                </span>
                <span className="text-xs text-[var(--theme-text-tertiary)] leading-snug">
                  {line.description}
                </span>
              </div>
            </ExternalLink>
          ))}
        </div>
      </div>

      {/* Kink-Aware Directories */}
      <div className="flex flex-col gap-2">
        <p className="text-xs font-semibold text-[var(--theme-text-secondary)] uppercase tracking-wide m-0">
          Kink-Aware Directories
        </p>
        <div className="flex flex-col gap-2">
          {DIRECTORIES.map((dir) => (
            <ExternalLink key={dir.name} href={dir.href} label={`Visit ${dir.name}`}>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold text-[var(--theme-text-primary)]">
                  {dir.name}
                </span>
                <span className="text-xs text-[var(--theme-text-tertiary)] leading-snug">
                  {dir.description}
                </span>
              </div>
            </ExternalLink>
          ))}
        </div>
      </div>

      {/* Educational Links */}
      <div className="flex flex-col gap-2">
        <p className="text-xs font-semibold text-[var(--theme-text-secondary)] uppercase tracking-wide m-0">
          Education
        </p>
        <div className="flex flex-col gap-2">
          {EDUCATIONAL_LINKS.map((link) => (
            <ExternalLink key={link.name} href={link.href} label={`Visit ${link.name}`}>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold text-[var(--theme-text-primary)]">
                  {link.name}
                </span>
                <span className="text-xs text-[var(--theme-text-tertiary)] leading-snug">
                  {link.description}
                </span>
              </div>
            </ExternalLink>
          ))}
        </div>
      </div>

      {/* Mandatory disclaimer */}
      <p className="text-xs text-[var(--theme-text-tertiary)] leading-snug m-0 pt-1 border-t border-[var(--theme-border-light)]">
        Helm does not endorse or verify any listed resource. These links are provided for
        informational purposes only. Helm does not provide crisis counseling or clinical services.
      </p>
    </div>
  );
};

export default ResourcesSection;
